import React, { useEffect, useState } from 'react';
import axios from 'axios';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Button,
  Chip,
  Fade,
  Grid,
  Paper,
  Popper,
  Avatar,
  List,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  ListItemSecondaryAction,
  Typography,
  ListItemButton,
  Badge,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress
} from '@mui/material';

// third party
import PerfectScrollbar from 'react-perfect-scrollbar';

// assets
import QueryBuilderTwoToneIcon from '@mui/icons-material/QueryBuilderTwoTone';
import CircleNotificationsOutlinedIcon from '@mui/icons-material/CircleNotificationsOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import { useSelector } from 'react-redux';

const NotificationSection = () => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [imageLoadError, setImageLoadError] = React.useState(false);
  const anchorRef = React.useRef(null);
  const timeoutRef = React.useRef(null);
const date = useSelector((state)=> state.customization.currentDate);

const newDate = new Date(date);
const formattedDate = newDate.toISOString().split("T")[0];

  // Get read notifications from session storage
  const getReadNotifications = () => {
    try {
      const stored = sessionStorage.getItem('readNotifications');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading from sessionStorage:', error);
      return [];
    }
  };

  // Save read notifications to session storage
  const saveReadNotifications = (readIds) => {
    try {
      sessionStorage.setItem('readNotifications', JSON.stringify(readIds));
    } catch (error) {
      console.error('Error saving to sessionStorage:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`http://localhost:8080/v2/notification/${notificationId}/read`);
      const readIds = getReadNotifications();
      if (!readIds.includes(notificationId)) {
        readIds.push(notificationId);
        saveReadNotifications(readIds);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      const readIds = getReadNotifications();
      if (!readIds.includes(notificationId)) {
        readIds.push(notificationId);
        saveReadNotifications(readIds);
      }
    }
  };

  // Fetch notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.post('https://neoe2e.neophyte.live/fm/v2/notification', {
          date: formattedDate
        });

        const readIds = getReadNotifications();

        const processedNotifications = response.data.map((notification) => {
          const isReadInBackend = notification.is_read === true;
          const isReadInSession = readIds.includes(notification._id);
          const isNew = !(isReadInBackend || isReadInSession);

          return {
            ...notification,
            isNew: isNew,
            imageLoadError: false
          };
        });

        setNotifications(processedNotifications);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setError(err.message || 'Failed to load notifications');
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [formattedDate]);
  console.log("date123", formattedDate)

  // Count unread notifications
  const unreadCount = notifications.filter((notification) => notification.isNew).length;

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  const handlePopperMouseEnter = () => {
    clearTimeout(timeoutRef.current);
  };

  const handlePopperMouseLeave = () => {
    setOpen(false);
  };

  const handleNotificationClick = async (notification) => {
    await markAsRead(notification._id);
    setNotifications((prev) => prev.map((item) => (item._id === notification._id ? { ...item, isNew: false } : item)));
    setSelectedNotification(notification);
    setDialogOpen(true);
    setImageLoadError(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setImageLoadError(false);
  };

  const handleImageError = () => {
    setImageLoadError(true);
  };

  const newNotifications = notifications.filter((n) => n.isNew);
  const earlierNotifications = notifications.filter((n) => !n.isNew);

  return (
    <>
      <Button
        sx={{
          minWidth: { sm: 50, xs: 35 },
          color: 'white'
        }}
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        aria-label="Notification"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : null}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              right: -3,
              top: 13,
              border: `2px solid ${theme.palette.background.paper}`,
              padding: '0 4px',
              fontSize: '0.75rem',
              minWidth: '18px',
              height: '18px'
            }
          }}
        >
          <CircleNotificationsOutlinedIcon
            sx={{
              fontSize: '2.0rem',
              color: '#0000FF',
              transition: 'transform 0.2s ease-in-out, color 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.1)',
                color: '#0000FF'
              }
            }}
          />
        </Badge>
      </Button>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 10] }
          },
          {
            name: 'preventOverflow',
            options: { altAxis: true }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper onMouseEnter={handlePopperMouseEnter} onMouseLeave={handlePopperMouseLeave}>
              <List
                sx={{
                  width: '100%',
                  maxWidth: 380,
                  minWidth: 300,
                  bgcolor: theme.palette.background.paper,
                  pb: 0,
                  borderRadius: '10px',
                  boxShadow: theme.shadows[8]
                }}
              >
                <PerfectScrollbar style={{ height: 400, overflowX: 'hidden' }}>
                  {loading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Loading notifications...
                      </Typography>
                    </Box>
                  ) : error ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <ErrorIcon color="error" sx={{ mb: 1 }} />
                      <Typography variant="body2" color="error">
                        {error}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {newNotifications.length > 0 && (
                        <>
                          <ListSubheader disableSticky>
                            <Chip size="small" color="primary" label="New" />
                          </ListSubheader>
                          {newNotifications.map((notification) => (
                            <NotificationItem
                              key={notification._id}
                              notification={notification}
                              onClick={handleNotificationClick}
                              theme={theme}
                            />
                          ))}
                        </>
                      )}

                      {earlierNotifications.length > 0 && (
                        <>
                          <ListSubheader disableSticky>
                            <Chip size="small" variant="outlined" label="Earlier" />
                          </ListSubheader>
                          {earlierNotifications.map((notification) => (
                            <NotificationItem
                              key={notification._id}
                              notification={notification}
                              onClick={handleNotificationClick}
                              theme={theme}
                              isEarlier
                            />
                          ))}
                        </>
                      )}

                      {notifications.length === 0 && (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No notifications available
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </PerfectScrollbar>
              </List>
            </Paper>
          </Fade>
        )}
      </Popper>

      <NotificationDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        notification={selectedNotification}
        theme={theme}
        imageLoadError={imageLoadError}
        onImageError={handleImageError}
      />
    </>
  );
};

const NotificationItem = ({ notification, onClick, theme, isEarlier = false }) => (
  <ListItemButton alignItems="flex-start" sx={{ pt: 0 }} onClick={() => onClick(notification)}>
    <ListItemAvatar>
      <Avatar
        sx={{
          bgcolor: isEarlier ? theme.palette.grey[300] : theme.palette.warning.main,
          color: isEarlier ? theme.palette.grey[600] : theme.palette.warning.contrastText
        }}
      >
        <SecurityIcon />
      </Avatar>
    </ListItemAvatar>
    <ListItemText
      primary={
        <Typography variant="subtitle1" sx={{ fontWeight: isEarlier ? 400 : 500 }}>
          Safety Alert - {notification.device_name}
        </Typography>
      }
      secondary={
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {[
              notification.ppe === false ? 'PPE' : null,
              notification.helmet === false ? 'Helmet' : null,
              notification.shoes === false ? 'Shoes' : null
            ].filter(Boolean).join(', ') + ' violation detected'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(notification.last_updated_time).toLocaleTimeString()}
          </Typography>
        </Box>
      }
    />
    <ListItemSecondaryAction sx={{ top: 22 }}>
      <Grid container justifyContent="flex-end">
        <Grid item>
          <QueryBuilderTwoToneIcon
            sx={{
              fontSize: '0.75rem',
              mr: 0.5,
              color: theme.palette.grey[400]
            }}
          />
        </Grid>
        <Grid item>
          <Typography variant="caption" display="block" gutterBottom sx={{ color: theme.palette.grey[400] }}>
            {Math.floor((new Date() - new Date(notification.last_updated_time)) / (1000 * 60))} minutes ago
          </Typography>
        </Grid>
      </Grid>
    </ListItemSecondaryAction>
  </ListItemButton>
);

const NotificationDialog = ({ open, onClose, notification }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '12px'
      }
    }}
  >
    <DialogTitle>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Safety Alert Details</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
    <DialogContent dividers>
      {notification && (
        <>
          <Box mb={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Device:</strong> {notification.device_name}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Time:</strong> {new Date(notification.last_updated_time).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Status:</strong>
                  <Chip
                    label={notification.ppe ? 'Compliant' : 'Violation'}
                    size="small"
                    color={notification.ppe ? 'success' : 'error'}
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>TrackID:</strong> {notification.track_id.toString() || 'Unknown'}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* <Box textAlign="center" mt={2}>
            <Typography variant="h6" gutterBottom>
              Captured Image
            </Typography>
            {notification.frame_image ? (
              <Box
                component="img"
                src={notification.frame_image}
                alt="Safety violation"
                onError={onImageError}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '8px',
                  boxShadow: theme.shadows[4],
                  border: `1px solid ${theme.palette.divider}`,
                  objectFit: 'contain'
                }}
              />
            
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.grey[200],
                  borderRadius: '8px',
                  p: 2
                }}
              >
                <ImageNotSupportedIcon sx={{ fontSize: 48, color: theme.palette.grey[500], mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  {imageLoadError ? 'Image not found or failed to load' : 'No image available'}
                </Typography>
              </Box>
            )}
          </Box> */}

        </>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary" variant="contained" sx={{ borderRadius: '8px' }}>
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

export default NotificationSection;